import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { CurrencyService, CurrencyCode } from '../../../services/core/currency/currency.service';

export type OnlyCurrency = 'base' | 'secondary';
export type PriceLayout = 'auto' | 'inline' | 'stack';

type MoneyParts = {
  integer: string;
  fraction: string;
  symbol: string;
};

@Component({
  selector: 'app-price',
  templateUrl: './price.component.html',
  styleUrls: ['./price.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() amount: unknown;
  @Input() currency?: CurrencyCode;
  @Input() dualDisplay?: boolean;
  @Input() only?: OnlyCurrency;
  @Input() layout: PriceLayout = 'auto';
  @Input() overflowBufferPx = 6;

  @ViewChild('wrap', { static: true }) private wrapRef!: ElementRef<HTMLElement>;

  private stackedByAuto = false;
  private rafId: number | null = null;

  constructor(
    private readonly currencyService: CurrencyService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.recalcAutoLayout();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['amount'] ||
      changes['currency'] ||
      changes['dualDisplay'] ||
      changes['only'] ||
      changes['layout']
    ) {
      this.recalcAutoLayout();
    }
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.recalcAutoLayout();
  }

  private recalcAutoLayout(): void {
    if (this.layout !== 'auto') {
      this.stackedByAuto = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;

      const el = this.wrapRef?.nativeElement;
      if (!el) { return; }

      const overflows = el.scrollWidth > el.clientWidth + this.overflowBufferPx;

      if (this.stackedByAuto !== overflows) {
        this.stackedByAuto = overflows;
        this.cdr.markForCheck();
      }
    });
  }

  get effectiveLayout(): Exclude<PriceLayout, 'auto'> {
    if (this.layout === 'inline') { return 'inline'; }
    if (this.layout === 'stack') { return 'stack'; }
    return this.stackedByAuto ? 'stack' : 'inline';
  }

  get shouldRender(): boolean {
    const n = this.currencyService.toNumber(this.amount);
    return Number.isFinite(n);
  }

  private get sourceCurrency(): CurrencyCode {
    return this.currency ?? this.currencyService.getBaseCurrency();
  }

  private get dualEnabled(): boolean {

    if (this.only) { return false; }

    return this.currencyService.shouldShowDual(this.dualDisplay);
  }

  get showSecondary(): boolean {
    if (!this.dualEnabled) { return false; }

    const base = this.currencyService.getBaseCurrency();
    const secondary = this.currencyService.getSecondaryCurrency();
    return base !== secondary;
  }

  get primaryParts(): MoneyParts {
    const num = this.currencyService.toNumber(this.amount);

    const base = this.currencyService.getBaseCurrency();
    const secondary = this.currencyService.getSecondaryCurrency();
    const from = this.sourceCurrency;
    const baseAmount = this.currencyService.convert(num, from, base);
    const target = this.only === 'secondary' ? secondary : base;
    const targetAmount =
      target === base ? baseAmount : this.currencyService.convert(baseAmount, base, secondary);

    return this.currencyService.formatParts(targetAmount, target);
  }

  get secondaryParts(): MoneyParts | null {
    if (!this.showSecondary) { return null; }

    const num = this.currencyService.toNumber(this.amount);

    const base = this.currencyService.getBaseCurrency();
    const secondary = this.currencyService.getSecondaryCurrency();
    const from = this.sourceCurrency;

    const baseAmount = this.currencyService.convert(num, from, base);
    const secondaryAmount = this.currencyService.convert(baseAmount, base, secondary);

    return this.currencyService.formatParts(secondaryAmount, secondary);
  }
}
